import {useContext} from 'react'
import {useGTMDispatch} from '../analytics/react-gtm-hook/index'
import {transformNavMenuPromoItemsForGA4, transformNavMenuPromoBannersForGA4, useSlotLocation, createPromoForViewPromotion} from './utils'
import { validateSlotActiveDays, isSlotWithReoccurringContent } from '../components/cms/utils'
import { usePromotionTrackingRegistry } from './promotion-tracking-context'
import { CurrentSlotPositionContext } from './current-slot-position-context'

export const usePromotionDataTracking = () => {
  const sendDataToGTM = useGTMDispatch()
  const location = useSlotLocation()
  const {registerTrackedPromotions} = usePromotionTrackingRegistry()

  const sendPromotionData = (content) => {
    const visibleSlots = content.reduce((acc, slotGroup) => {
      const filtered = slotGroup.filter((promotion) => !isSlotWithReoccurringContent(promotion) || validateSlotActiveDays(promotion))
      if (filtered.length > 0) acc.push(filtered)
      return acc
    }, [])

    try {
      const promotions = createPromoForViewPromotion(visibleSlots)
      if (!promotions || promotions.length === 0) return

      // Build the canonical items the view event will dispatch. These are
      // also cached so the select_promotion handler can reuse the exact
      // same slot_position / link_position / creative_slot values.
      const promotionData = promotions.map((promotion) => ({
        creative_name: promotion.creative_name,
        creative_slot: `${location}_${promotion.creative_slot}`,
        promotion_id: promotion.promotion_id,
        promotion_name: promotion.promotion_name,
        link_position: promotion.link_position,
        slot_position: promotion.slot_position,
        campaign_ID: promotion.campaign_ID,
      }))

      registerTrackedPromotions(promotionData)

      setTimeout(() => {
        const eventData = {
          event: 'view_promotion',
          ecommerce: {
            items: promotionData,
          },
        }
  
        if (Object.keys(eventData.ecommerce.items).length > 0) {
          sendDataToGTM(eventData)
        }
      }, 0)
    } catch (error) {
        console.error('usePromotionDataTracking error: ' + error.message)
    }
  }

  return sendPromotionData
}

/**
 * Populates the promotion tracking registry without dispatching a
 * `view_promotion` GTM event.
 *
 * Use this from a page when you want the registry primed eagerly on mount
 * (so click handlers can look up the canonical slot_position / link_position),
 * while leaving the actual `view_promotion` dispatch to in-view tracking
 * lower down the tree (see `DisplaySlots`).
 */
export const useRegisterPromotionsForTracking = () => {
  const location = useSlotLocation()
  const {registerTrackedPromotions} = usePromotionTrackingRegistry()

  return (content) => {
    const visibleSlots = content.reduce((acc, slotGroup) => {
      const filtered = slotGroup.filter((promotion) => !isSlotWithReoccurringContent(promotion) || validateSlotActiveDays(promotion))
      if (filtered.length > 0) acc.push(filtered)
      return acc
    }, [])

    try {
      const promotions = createPromoForViewPromotion(visibleSlots)
      if (!promotions || promotions.length === 0) return

      const promotionData = promotions.map((promotion) => ({
        creative_name: promotion.creative_name,
        creative_slot: `${location}_${promotion.creative_slot}`,
        promotion_id: promotion.promotion_id,
        promotion_name: promotion.promotion_name,
        link_position: promotion.link_position,
        slot_position: promotion.slot_position,
        campaign_ID: promotion.campaign_ID,
      }))

      registerTrackedPromotions(promotionData)
    } catch (error) {
      console.error('useRegisterPromotionsForTracking error: ' + error.message)
    }
  }
}

export const useSelectPromotionTracking = () => {
  const sendDataToGTM = useGTMDispatch()
  const location = useSlotLocation()
  const {findTrackedPromotion} = usePromotionTrackingRegistry()
  // Read the authoritative slot position that SlotContent set via context.
  // This is always the page-level position regardless of what any individual
  // component or wrapper passes in promotion.slot_position.
  const slotPositionFromContext = useContext(CurrentSlotPositionContext)

  const sendSelectPromotion = (promotion) => {
    if (!promotion) return

    try {
      // Values the leaf component supplied. These are used as fallbacks
      // when no matching tracked promotion is found in the registry.
      const fallbackCreativeSlot = `${location}_${promotion.creative_slot}`

      const trackedPromotion = findTrackedPromotion({
        creative_name: promotion.creative_name,
        promotion_id: promotion.promotion_id,
        creative_slot: fallbackCreativeSlot,
        slot_position: slotPositionFromContext ?? promotion.slot_position,
      })

      const promotionItem = {
        creative_name: promotion.creative_name,
        creative_slot: trackedPromotion?.creativeSlot ?? fallbackCreativeSlot,
        promotion_id: promotion.promotion_id,
        promotion_name: promotion.promotion_name,
        link_position: trackedPromotion?.linkPosition ?? promotion.link_position,
        slot_position: trackedPromotion?.slotPosition ?? promotion.slot_position,
        campaign_ID: promotion.campaign_ID,
      }

      setTimeout(() => {
        const eventData = {
          event: 'select_promotion',
          ecommerce: {
            items: [promotionItem],
          },
        }
        
        if (Object.keys(eventData.ecommerce.items).length > 0) {
          sendDataToGTM(eventData)
        }
      }, 0)
    } catch (error) {
      console.error('useSelectPromotionTracking error: ' + error.message)
    }
  }
  return sendSelectPromotion
}

export const useNavMenuPromotionDataTracking = () => {
  const sendDataToGTM = useGTMDispatch()

  const sendNavMenuPromotionData = (promotions, navMenuLevel, hasNavFolders) => {
    if (!promotions) return
    setTimeout(() => {
      try {
        const promotionData = transformNavMenuPromoItemsForGA4(promotions, navMenuLevel, hasNavFolders)
        const eventData = {
          event: 'view_promotion',
          ecommerce: {
            items: promotionData,
          },
        }

        if (Object.keys(eventData.ecommerce.items).length > 0) {
          sendDataToGTM(eventData)
        }
      } catch (error) { 
        console.error('useNavMenuPromotionDataTracking error within the setTimeout: ' + error.message)
      }
    }, 0)
  }
  return sendNavMenuPromotionData
}

export const useNavMenuPromoBannersDataTracking = () => {
  const sendDataToGTM = useGTMDispatch()

  const sendNavMenuPromoBannersData = (flyOutContent) => {
    if (!flyOutContent) return
    setTimeout(() => {
      try {
        const promotionData = transformNavMenuPromoBannersForGA4(flyOutContent)
        const eventData = {
          event: 'view_promotion',
          ecommerce: {
            items: promotionData,
          },
        }

        if (Object.keys(eventData.ecommerce.items).length > 0) {
          sendDataToGTM(eventData)
        }
      } catch (error) { 
        console.error('useNavMenuPromoBannersDataTracking error within the setTimeout: ' + error.message)
      }
    }, 0)
  }
  return sendNavMenuPromoBannersData
}

export const useSelectNavMenuPromotionTracking = () => {
  const sendDataToGTM = useGTMDispatch()

  const sendNavMenuSelectPromotion = (name, path, categoryId, navMenuLevel, navFolderId, index) => {
    if (!name) return

    let creativeName = categoryId || navFolderId || name || 'No category ID';
    if (creativeName === undefined || creativeName === 'undefined') {
      creativeName = 'No category ID';
    }
    
    setTimeout(() => {
      try {
        const promotionData = [
          {
            creative_name: creativeName,
            creative_slot: navMenuLevel,
            promotion_id: path ?? 'Nav folder',
            promotion_name: name,
            link_position: index ?? 1
          }
        ]
        const eventData = {
          event: 'select_promotion',
          ecommerce: {
            items: promotionData,
          },
        }

        if (Object.keys(eventData.ecommerce.items).length > 0) {
          sendDataToGTM(eventData)
        }
      } catch (error) { 
        console.error('useSelectNavMenuPromotionTracking error within the setTimeout: ' + error.message)
      }
    }, 0)
  }
  return sendNavMenuSelectPromotion
}

export const useVoucherBannerPromotionTracking = (promotionData) => {
  const sendDataToGTM = useGTMDispatch()

  const buildPromotionData = (promotionData, eventType) => {

    const formattedPromotionData = {
      creative_name: promotionData.creative_name,
      creative_slot: promotionData.creative_slot,
      promotion_name: promotionData.promotion_name,
      slot_position: 'navigation_notification_bar',
      campaign_ID: promotionData.campaign_ID
    }

    if(eventType === 'select') {
      formattedPromotionData.promotion_id = promotionData.promotion_id
      formattedPromotionData.location_id = promotionData.location_id
      formattedPromotionData.banner_action_status = promotionData.banner_action_status
      formattedPromotionData.link_position = promotionData.link_position
    }

    if(eventType === 'dismiss') {
      formattedPromotionData.banner_action_status = 'dismissed'
    }

    return [formattedPromotionData]
  }

  const sendViewVoucherBanner = (promotionData) => {
    setTimeout(() => {
      try {
        const formattedData = buildPromotionData(promotionData, 'view')
        const eventData = {
          event: 'view_promotion',
          ecommerce: {
            items: formattedData
          }
        }

        sendDataToGTM(eventData)
      } catch (error) {
        console.error(
          'useVoucherBannerPromotionTracking - sendViewVoucherBanner error within the setTimeout: ' + error.message
        )
      }
    }, 0)
  }

  const sendSelectVoucherBanner = (promotionData) => {
    setTimeout(() => {
      try {
        const formattedData = buildPromotionData(promotionData, 'select')
        const eventData = {
          event: 'select_promotion',
          ecommerce: {
            items: formattedData
          }
        }

        sendDataToGTM(eventData)
      } catch (error) {
        console.error(
          'useVoucherBannerPromotionTracking - sendSelectVoucherBanner error within the setTimeout: ' + error.message
        )
      }
    }, 0)
  }

  const sendDismissedVoucherBanner = (promotionData) => {
    setTimeout(() => {
      try {
        const formattedData = buildPromotionData(promotionData, 'dismiss')
        const eventData = {
          event: 'dismiss_promotion',
          ecommerce: {
            items: formattedData
          }
        }

        sendDataToGTM(eventData)
      } catch (error) {
        console.error(
          'useVoucherBannerPromotionTracking - sendDismissedVoucherBanner error within the setTimeout: ' + error.message
        )
      }
    }, 0)
  }

  return [sendViewVoucherBanner, sendSelectVoucherBanner, sendDismissedVoucherBanner]
}

export const voucherAnalyticsDataVariables = {
  view_offers_button: {
    banner_action_status: 'promotion_selected',
    promotion_id: '/offers',
    link_position: 2
  },
  terms_link: {
    banner_action_status: 'terms_viewed',
    promotion_id: '/terms.html#discounts-coupons',
    link_position: 1
  },
  copy_button: {
    banner_action_status: 'voucher_copied',
    promotion_id: '',
    link_position: 2
  }
}